import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  getUsersByOrganization,
  getAllUsers,
  getUserById,
  getUserOrganizations,
  createUser,
  updateUser,
  resetUserPassword,
  updateUserStatus,
  deleteUser,
} from '../api/users.api.ts';
import { addUserToOrg, removeUserFromOrg, setUserDefaultOrg, updateMemberRole } from '../api/organizations.api.ts';
import { adminQueryKeys } from './queryKeys.ts';
import { getErrorMessage } from '../utils/errorUtils.ts';
import type {
  User,
  UserOrganization,
  CreateUserRequest,
  AddUserToOrgRequest,
  UpdateMemberRoleRequest,
  ResetUserPasswordRequest,
  UpdateUserStatusRequest,
} from '../types.ts';

/** Fetches all users (admin list). Use for "Add user to org" when backend supports GET /api/admin/users. */
export const useAllUsers = (): UseQueryResult<User[], Error> => {
  return useQuery({
    queryKey: [...adminQueryKeys.users.all, 'all'] as const,
    queryFn: async () => {
      const response = await getAllUsers();
      return response.data;
    },
    staleTime: 60000,
  });
};

/** Fetches users for an organization via /api/admin/organizations/{orgId}/members */
export const useUsers = (organizationId: string | undefined): UseQueryResult<User[], Error> => {
  return useQuery({
    queryKey: adminQueryKeys.users.list(organizationId),
    queryFn: async () => {
      const response = await getUsersByOrganization(organizationId!);
      return response.data;
    },
    enabled: !!organizationId,
    staleTime: 30000, // 30 seconds
  });
};

export const useUser = (id: number): UseQueryResult<User, Error> => {
  return useQuery({
    queryKey: adminQueryKeys.users.detail(id),
    queryFn: async () => {
      const response = await getUserById(id);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useUserOrganizations = (id: number): UseQueryResult<UserOrganization[], Error> => {
  return useQuery({
    queryKey: [...adminQueryKeys.users.detail(id), 'organizations'] as const,
    queryFn: async () => {
      const response = await getUserOrganizations(id);
      return response.data;
    },
    enabled: !!id,
  });
};

export const useCreateUser = (): UseMutationResult<User, Error, CreateUserRequest> => {
  const queryClient = useQueryClient();

  return useMutation<User, Error, CreateUserRequest>({
    mutationFn: async (payload: CreateUserRequest) => {
      const response = await createUser(payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.all, 'all'] });
      if (variables.organization_id) {
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(variables.organization_id) });
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(variables.organization_id) });
      }
      toast.success('User created successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create user'));
    },
  });
};

export const useUpdateUser = (): UseMutationResult<User, Error, { id: number; payload: Partial<CreateUserRequest> }> => {
  const queryClient = useQueryClient();

  return useMutation<User, Error, { id: number; payload: Partial<CreateUserRequest> }>({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<CreateUserRequest> }) => {
      const response = await updateUser(id, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.id) });
      toast.success('User updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update user'));
    },
  });
};

export const useResetUserPassword = (): UseMutationResult<
  { message: string },
  Error,
  { id: number; payload: ResetUserPasswordRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, { id: number; payload: ResetUserPasswordRequest }>({
    mutationFn: async ({ id, payload }) => resetUserPassword(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.id) });
      toast.success('Password updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update password'));
    },
  });
};

export const useUpdateUserStatus = (): UseMutationResult<
  User,
  Error,
  { id: number; payload: UpdateUserStatusRequest }
> => {
  const queryClient = useQueryClient();

  return useMutation<User, Error, { id: number; payload: UpdateUserStatusRequest }>({
    mutationFn: async ({ id, payload }) => {
      const response = await updateUserStatus(id, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.lists() });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.detail(variables.id) });
      toast.success('User status updated successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update user status'));
    },
  });
};

export const useAddUserToOrganizationFromUserDetail = (): UseMutationResult<
  unknown,
  Error,
  { organizationId: string; payload: AddUserToOrgRequest; userId: number }
> => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { organizationId: string; payload: AddUserToOrgRequest; userId: number }>({
    mutationFn: async ({ organizationId, payload }) => addUserToOrg(organizationId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.detail(variables.userId), 'organizations'] });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.all, 'all'] });
      toast.success('Organization added to user');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to add organization to user'));
    },
  });
};

export const useRemoveUserOrganizationFromUserDetail = (): UseMutationResult<
  { message: string },
  Error,
  { organizationId: string; userId: number }
> => {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, { organizationId: string; userId: number }>({
    mutationFn: async ({ organizationId, userId }) => removeUserFromOrg(organizationId, String(userId)),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.detail(variables.userId), 'organizations'] });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.all, 'all'] });
      toast.success('Organization removed from user');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove organization from user'));
    },
  });
};

export const useSetDefaultUserOrganizationFromUserDetail = (): UseMutationResult<
  void,
  Error,
  { organizationId: string; userId: number }
> => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { organizationId: string; userId: number }>({
    mutationFn: async ({ organizationId, userId }) => setUserDefaultOrg(organizationId, String(userId)),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.detail(variables.userId), 'organizations'] });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.all, 'all'] });
      toast.success('Default organization updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to set default organization'));
    },
  });
};

export const useUpdateUserOrganizationRoleFromUserDetail = (): UseMutationResult<
  unknown,
  Error,
  { organizationId: string; userId: number; payload: UpdateMemberRoleRequest }
> => {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { organizationId: string; userId: number; payload: UpdateMemberRoleRequest }>({
    mutationFn: async ({ organizationId, userId, payload }) =>
      updateMemberRole(organizationId, String(userId), payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.detail(variables.userId), 'organizations'] });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(variables.organizationId) });
      queryClient.invalidateQueries({ queryKey: [...adminQueryKeys.users.all, 'all'] });
      toast.success('User role updated');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update role'));
    },
  });
};

export const useDeleteUser = (organizationId: string | undefined): UseMutationResult<{ message: string }, Error, number> => {
  const queryClient = useQueryClient();

  return useMutation<{ message: string }, Error, number>({
    mutationFn: async (memberId: number) => {
      if (!organizationId) throw new Error('Organization is required to delete a user');
      const response = await deleteUser(organizationId, memberId);
      return response;
    },
    onSuccess: () => {
      if (organizationId) {
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.list(organizationId) });
        queryClient.invalidateQueries({ queryKey: adminQueryKeys.organizations.members(organizationId) });
      }
      queryClient.invalidateQueries({ queryKey: adminQueryKeys.users.all });
      toast.success('User deleted successfully');
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to remove user'));
    },
  });
};
